import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { AddressMergerApplyInput, AddressMergerApplyResult, AddressMergerRecord, AddressMergerScanResult, AddressTarget } from '@crm/shared';
import { api } from '../../api/client';

export interface AddressMergerState {
  target: AddressTarget;
  results: AddressMergerRecord[];
  scanned: number;
  finishedAt: string;
}

const APPLY_BATCH = 100;

const key = (target: AddressTarget) => ['address-merger-scan', target];

/** The last Address Merger scan per target, kept in the query cache like the other clean up results. */
export function useAddressMergerResults(target: AddressTarget) {
  const qc = useQueryClient();
  const query = useQuery<AddressMergerState | null>({ queryKey: key(target), queryFn: () => qc.getQueryData<AddressMergerState>(key(target)) ?? null, enabled: false, staleTime: Infinity, gcTime: 60 * 60_000 });
  return { data: query.data ?? null, set: (v: AddressMergerState | null) => qc.setQueryData(key(target), v) };
}

export function useScanAddresses() {
  return useMutation({
    mutationFn: async ({ target, onProgress }: { target: AddressTarget; onProgress?: (p: { scanned: number; total: number; found: number }) => void }) => {
      const results: AddressMergerRecord[] = [];
      let after: string | null = null;
      let scanned = 0;
      let total = 0;
      do {
        const r: AddressMergerScanResult = (await api.post<AddressMergerScanResult>('/cleanup/addresses/scan', { target, after })).data;
        total = r.total ?? total;
        scanned += r.scanned;
        results.push(...r.results);
        onProgress?.({ scanned, total, found: results.length });
        after = r.next;
      } while (after);
      return { target, results, scanned, finishedAt: new Date().toISOString() } satisfies AddressMergerState;
    },
  });
}

/** Re-scans some records, 1,000 per request; records with nothing left to move are missing from the answer. */
export function useRecheckAddresses() {
  return useMutation({
    mutationFn: async ({ target, ids }: { target: AddressTarget; ids: string[] }) => {
      const out: AddressMergerRecord[] = [];
      for (let i = 0; i < ids.length; i += 1000) out.push(...(await api.post<AddressMergerScanResult>('/cleanup/addresses/scan', { target, ids: ids.slice(i, i + 1000) })).data.results);
      return out;
    },
  });
}

export interface AddressMoveProgress {
  target: AddressTarget;
  done: number;
  total: number;
}

const MOVE_KEY = ['address-merger-move'];

/** The move in progress, kept in the query cache so it still shows after leaving the page and coming back. */
export function useAddressMoveProgress() {
  const qc = useQueryClient();
  const query = useQuery<AddressMoveProgress | null>({ queryKey: MOVE_KEY, queryFn: () => qc.getQueryData<AddressMoveProgress>(MOVE_KEY) ?? null, enabled: false, staleTime: Infinity, gcTime: Infinity });
  return { data: query.data ?? null, set: (v: AddressMoveProgress | null) => qc.setQueryData(MOVE_KEY, v) };
}

/** Moves the chosen addresses 100 at a time (each one carries its source fields); `onProgress` gets how many are done. */
export function useApplyAddresses() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ target, items, removeFromSource, onProgress }: AddressMergerApplyInput & { onProgress?: (done: number) => void }) => {
      const total: AddressMergerApplyResult = { records: 0, added: 0, merged: 0, skipped: [] };
      for (let i = 0; i < items.length; i += APPLY_BATCH) {
        const r = (await api.post<AddressMergerApplyResult>('/cleanup/addresses/apply', { target, items: items.slice(i, i + APPLY_BATCH), removeFromSource })).data;
        total.records += r.records;
        total.added += r.added;
        total.merged += r.merged;
        total.skipped.push(...r.skipped);
        onProgress?.(Math.min(items.length, i + APPLY_BATCH));
      }
      return total;
    },
    onSuccess: () => ['contacts', 'contact', 'organizations', 'organization', 'no-contact-info'].forEach((k) => void qc.invalidateQueries({ queryKey: [k] })),
  });
}
